/**
 * useOfflineQueue — detecta conexión y guarda actualizaciones de status
 * en localStorage cuando el chofer no tiene señal.
 * Al recuperar internet, sincroniza la cola en orden.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { apiActualizarStatusOrden, apiCompletarOrden } from '../api';

const QUEUE_KEY = 'choyero_offline_queue';

function loadQueue() {
  try { return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]'); } catch { return []; }
}
function saveQueue(q) {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch {}
}

export function useOfflineQueue() {
  const [isOnline, setIsOnline]   = useState(() => navigator.onLine);
  const [syncing, setSyncing]     = useState(false);
  const [queueSize, setQueueSize] = useState(() => loadQueue().length);
  const [lastDropped, setLastDropped] = useState(null); // item que se descartó tras fallar la sync (no-red)
  const syncingRef = useRef(false);

  const enqueue = useCallback((item) => {
    const q = loadQueue();
    // Evitar duplicados exactos (mismo ordenId + status seguidos)
    const last = q[q.length - 1];
    if (last && last.action === item.action && last.ordenId === item.ordenId && last.status === item.status) return;
    q.push({ ...item, _qid: `${Date.now()}-${Math.random().toString(36).slice(2)}` });
    saveQueue(q);
    setQueueSize(q.length);
  }, []);

  const processQueue = useCallback(async () => {
    if (syncingRef.current) return;
    const q = loadQueue();
    if (q.length === 0) return;

    syncingRef.current = true;
    setSyncing(true);

    const pending = [...q];
    for (let i = 0; i < pending.length; i++) {
      const item = pending[i];
      try {
        if (item.action === 'updateStatus') {
          await apiActualizarStatusOrden(item.ordenId, item.status);
        } else if (item.action === 'completarOrden') {
          await apiCompletarOrden(item.ordenId, { evidencias: item.evidencias, firma: item.firma });
        }
        // Éxito: remover de la cola persistida
        pending.splice(i, 1);
        i--;
        saveQueue(pending);
        setQueueSize(pending.length);
      } catch (err) {
        if (!navigator.onLine) {
          // Perdimos señal durante la sync — detener y reintentar al volver
          break;
        }
        // Error HTTP (4xx, datos inválidos) — eliminar para no bloquear la cola, pero avisar:
        // este paso NUNCA se guardó en el servidor y antes se perdía en silencio.
        pending.splice(i, 1);
        i--;
        saveQueue(pending);
        setQueueSize(pending.length);
        setLastDropped({ ...item, motivo: err?.message || 'Error al sincronizar' });
      }
    }

    syncingRef.current = false;
    setSyncing(false);
  }, []);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      processQueue();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Si arranca online con cola pendiente (sesión anterior sin señal)
    if (navigator.onLine && loadQueue().length > 0) processQueue();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [processQueue]);

  return { isOnline, enqueue, syncing, queueSize, lastDropped };
}

export default useOfflineQueue;
